// utils/mapAccessPointSchedule.ts

export type AccessPointSchedule = {
  access_point: number;
  week_days: string[];
  start_time: string | null;
  end_time: string | null;
  /** @deprecated use exit_restriction_from */
  exit_restriction: string | null;
  exit_restriction_from: string | null;
  exit_restriction_to: string | null;
};

const parseTimeToMinutes = (time: string | null): number | null => {
  if (!time) return null;
  const [hours, minutes, seconds = 0] = time.split(":").map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  return hours * 60 + minutes + seconds / 60;
};

const mergeEarliestTime = (
  current: string | null,
  next: string | null,
): string | null => {
  if (!current) return next;
  if (!next) return current;

  const currentMinutes = parseTimeToMinutes(current);
  const nextMinutes = parseTimeToMinutes(next);

  if (currentMinutes === null) return next;
  if (nextMinutes === null) return current;

  return currentMinutes <= nextMinutes ? current : next;
};

const mergeLatestTime = (
  current: string | null,
  next: string | null,
): string | null => {
  if (!current) return next;
  if (!next) return current;

  const currentMinutes = parseTimeToMinutes(current);
  const nextMinutes = parseTimeToMinutes(next);

  if (currentMinutes === null) return next;
  if (nextMinutes === null) return current;

  return currentMinutes >= nextMinutes ? current : next;
};

const mergeSchedules = (
  current: AccessPointSchedule,
  next: AccessPointSchedule,
): AccessPointSchedule => ({
  access_point: current.access_point,
  week_days: Array.from(new Set([...current.week_days, ...next.week_days])),
  start_time: mergeEarliestTime(current.start_time, next.start_time),
  end_time: mergeLatestTime(current.end_time, next.end_time),
  exit_restriction: mergeLatestTime(
    current.exit_restriction_from || current.exit_restriction,
    next.exit_restriction_from || next.exit_restriction,
  ),
  exit_restriction_from: mergeEarliestTime(
    current.exit_restriction_from || current.exit_restriction,
    next.exit_restriction_from || next.exit_restriction,
  ),
  exit_restriction_to: mergeLatestTime(
    current.exit_restriction_to,
    next.exit_restriction_to,
  ),
});

const upsertSchedule = (
  scheduleByAccessPoint: Map<number, AccessPointSchedule>,
  accessPointId: number,
  schedule: Omit<AccessPointSchedule, "access_point">,
) => {
  const nextSchedule: AccessPointSchedule = {
    access_point: accessPointId,
    ...schedule,
  };

  const existing = scheduleByAccessPoint.get(accessPointId);
  scheduleByAccessPoint.set(
    accessPointId,
    existing ? mergeSchedules(existing, nextSchedule) : nextSchedule,
  );
};

const normalizeAccessPoints = (accessPoints: unknown): { id: number }[] => {
  if (Array.isArray(accessPoints)) {
    return accessPoints.filter(
      (accessPoint): accessPoint is { id: number } =>
        typeof (accessPoint as { id?: number })?.id === "number",
    );
  }

  if (
    accessPoints &&
    typeof accessPoints === "object" &&
    Array.isArray((accessPoints as { data?: unknown }).data)
  ) {
    return ((accessPoints as { data: { id?: number }[] }).data ?? []).filter(
      (accessPoint): accessPoint is { id: number } =>
        typeof accessPoint?.id === "number",
    );
  }

  return [];
};

export const mapAccessPointSchedule = (
  groups: any[],
): AccessPointSchedule[] => {
  if (!Array.isArray(groups)) return [];

  const scheduleByAccessPoint = new Map<number, AccessPointSchedule>();

  for (const group of groups) {
    const attrs = group?.attributes ?? group ?? {};
    const configurations = Array.isArray(attrs?.configuration)
      ? attrs.configuration
      : Array.isArray(group?.configuration)
        ? group.configuration
        : [];
    const accessPoints = normalizeAccessPoints(
      attrs?.access_points ?? group?.access_points,
    );
    const groupAccessPointIds = accessPoints.map(
      (accessPoint) => accessPoint.id,
    );

    for (const config of configurations) {
      const schedule = {
        week_days: config?.week_days ?? [],
        start_time: config?.start_time ?? null,
        end_time: config?.end_time ?? null,
        exit_restriction:
          config?.exit_restriction_from ?? config?.exit_restriction ?? null,
        exit_restriction_from:
          config?.exit_restriction_from ?? config?.exit_restriction ?? null,
        exit_restriction_to: config?.exit_restriction_to ?? null,
      };

      if (typeof config?.id === "number") {
        upsertSchedule(scheduleByAccessPoint, config.id, schedule);
        continue;
      }

      // Compatibilidad: reglas sin id de punto de acceso aplican a todos los AP del grupo.
      for (const accessPointId of groupAccessPointIds) {
        upsertSchedule(scheduleByAccessPoint, accessPointId, schedule);
      }
    }
  }

  return Array.from(scheduleByAccessPoint.values());
};
